'use client';

import { Box, Chip, type SxProps, type Theme } from '@mui/material';

export type RegionChipsProps = {
    regions: string[];
    /** Le conteneur le porte ; le compteur reçoit `${id}-more`. */
    'data-testid'?: string;
    sx?: SxProps<Theme>;
};

/**
 * Régions d'un item de catalogue, en chips.
 *
 * Une région → un chip. Plusieurs → le premier reste visible et les suivants se
 * replient derrière un compteur « +N » qui les déploie **au survol du compteur
 * lui-même**, dans un panneau au-dessus.
 *
 * C'est le mécanisme retenu entre les deux que ce composant remplace. L'autre —
 * celui des cards Produit — se déployait au survol de **la card entière**, et
 * son CSS de révélation vivait dans le `sx` du parent, qui ciblait
 * `[data-region-extra]` et `[data-region-more]` : un composant dont le
 * comportement était implémenté par son appelant, donc silencieusement cassé
 * dès qu'on le posait ailleurs. Se déployer au survol de la card était par
 * ailleurs gênant, la card étant un lien : viser le clic déployait les chips.
 *
 * Tous les chips restent dans le DOM — le repli est purement visuel — donc
 * lisibles par les technologies d'assistance et par les tests. Le compteur
 * porte la liste complète en `title`, reprise de l'implémentation Produit.
 */
export function RegionChips({ regions, 'data-testid': dataTestId, sx }: RegionChipsProps) {
    if (regions.length === 0) return null;

    const [first, ...rest] = regions;
    const hasMore = rest.length > 0;

    return (
        <Box
            data-testid={dataTestId}
            sx={[
                {
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    maxWidth: '100%',
                    '& [data-region-popup]': {
                        opacity: 0,
                        transform: 'translateY(4px)',
                        transition: 'opacity .2s ease, transform .2s ease',
                        pointerEvents: 'none',
                    },
                    '&:has([data-region-more]:hover) [data-region-popup]': {
                        opacity: 1,
                        transform: 'translateY(0)',
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {hasMore && (
                <Box
                    data-region-popup
                    sx={{
                        position: 'absolute',
                        bottom: 'calc(100% + 4px)',
                        left: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0.5,
                        zIndex: 1,
                    }}
                >
                    {rest.map((region) => (
                        <Chip
                            key={region}
                            label={region}
                            size="small"
                            variant="outlined"
                            sx={{ bgcolor: 'background.paper' }}
                        />
                    ))}
                </Box>
            )}
            <Chip label={first} size="small" variant="outlined" sx={{ maxWidth: '100%' }} />
            {hasMore && (
                <Chip
                    data-region-more
                    data-testid={dataTestId === undefined ? undefined : `${dataTestId}-more`}
                    label={`+${rest.length}`}
                    title={rest.join(', ')}
                    size="small"
                    variant="outlined"
                />
            )}
        </Box>
    );
}
